import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CommonGlobalService, GroupedOption, MultiSelect, Option, RootState, UUID } from '@umanforge/common';

import { ChartSectionService } from 'services/chartSection.service';
import { ChartOfSectionService } from 'services/chartOfSection.service';

import { ChartOfSectionApi } from 'interfaces/chartOfSection.interface';

import { MAX_VISU_CHART_BY_EVENT_MANAGER } from 'constants/visualisationGlobal.constants';


interface EventManagerVisualisationSettingProps {
  eventManagerId: UUID
  visuChartsId: UUID[]
  setVisuChartsId: ( value: UUID[] ) => void;
}

const EventManagerVisualisationSetting: React.FC<EventManagerVisualisationSettingProps> = ( { eventManagerId, visuChartsId, setVisuChartsId } ) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ allowedCharts, setAllowedCharts ] = useState<GroupedOption<{ id: UUID, name: string }>[]>( [] );
  const [ selectedCharts, setSelectedCharts ] = useState<Option<{ id: UUID, name: string }>[]>( [] );

  const [ isReady, setIsReady ] = useState<boolean>( false );


  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    if ( isReady ) {
      const newAllowedCharts = [];
      for ( const chart of allowedCharts ) {
        const isDisable = visuChartsId.length >= MAX_VISU_CHART_BY_EVENT_MANAGER;
        newAllowedCharts.push( { label: chart.label, options: CommonGlobalService.toOptions( chart.options.map( item => { return item.object; } ), undefined, isDisable ) } );
      }

      const charts = newAllowedCharts
        .map( group => { return group.options; } )
        .flat();
      const filteredCharts = charts
        .filter( chart => { return visuChartsId.some( chartId => { return chartId === chart.object.id; } ); } )
        .map( chart => { return { ...chart, isDisabled: false }; } );

      setAllowedCharts( newAllowedCharts );
      setSelectedCharts( filteredCharts );
    }
  }, [ visuChartsId, isReady ] );


  /*----------------------------- Function ------------------------------*/
  const init = () => {
    getUpdatedAllowCharts()
      .then( groupedCharts => {
        const allCharts = groupedCharts.map( groupedChart => { return groupedChart.options; } ).flat();
        const filteredCharts = allCharts.filter( chart => { visuChartsId.some( chartId => { return chartId === chart.object.id; } ); } );
        setAllowedCharts( groupedCharts );

        if ( filteredCharts.length != visuChartsId.length ) {
          onSelectedChartsChanged( filteredCharts );
        } else {
          setSelectedCharts( filteredCharts );
        }
        setIsReady( true );
      } );
  };

  const getUpdatedAllowCharts = (): Promise<GroupedOption<{ id: UUID, name: string }>[]> => {
    return new Promise( resolve => {
      ChartSectionService.getByEventManager( eventManagerId, token, dispatch )
        .then( chartSections => {
          Promise.all( chartSections.map( async chartSection => {
            const charts = await ChartOfSectionService.getBySection( chartSection.id, token, dispatch );
            const formattedChart = charts.map( chart => { return formatChart( chart ); } );
            return { label: chartSection.name, options: CommonGlobalService.toOptions( formattedChart ) };
          } ) )
            .then( groupedOptions => {
              resolve( groupedOptions );
            } );
        } );
    } );
  };

  const formatChart = ( chart: ChartOfSectionApi ) => {
    return { id: chart.id, name: chart.title };
  };

  const onSelectedChartsChanged = ( charts: Option<{ id: UUID, name: string }>[] ) => {
    setSelectedCharts( charts );
    setVisuChartsId( charts.map( chart => { return chart.object.id; } ) );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-bold mb-5">{ t( 'translation:global_visualisation' ) }</h3>

      <MultiSelect
        label={ t( 'translation:graphique' ) }
        placeholder={ t( 'no_associated_graphiques' ) }
        getValues={ allowedCharts }
        selectedValues={ selectedCharts }
        clearable
        onChange={ items => {
          onSelectedChartsChanged( items );
        } }
      />

    </div>
  );
};

export default EventManagerVisualisationSetting;
